import { jsonToFormData } from 'util/jsonToFormData';
import { selectApiData } from 'util/selector/apiSelector';
import { selectResponseData } from 'util/selector/axiosSelector';

import { WithData } from 'api/interfaces';
import { AxiosInstance } from 'axios';

import { CompanyDto, CompanyPostEntry } from './dto/Company.dto';
import { ProjectPaginatedDto } from './dto/Project.dto';

export namespace CompanyApi {
  export type EnabledLocales = {
    enabledLocales: string[];
  };

  export type GetListResponse = CompanyDto[];

  export type CompanyProfile = {
    name: string;
    context: string;
    definitions: Record<string, string>;
    toneOfVoice: string;
  };

  export type MergeSyncRun = {
    status: string;
    processedCount: number;
    scheduledAt: string | null;
    finishedAt: string | null;
    errorMessage: string | null;
  };

  export type MergeConnection = {
    id: string;
    name: string;
    lastSyncRun: MergeSyncRun | null;
    nextSyncRun: MergeSyncRun | null;
  };

  export type MergeLinkToken = {
    linkToken: string;
  };

  export type HrisDemographyCustomField = {
    id: string;
    displayName: string;
    enumeration: string[];
  };

  export type HrisClassificationValueMapping = {
    demographyClassificationId: number;
    hrisValue: string;
  };

  export type HrisDemographyCustomFieldMapping = {
    id: string;
    demographyCustomField: HrisDemographyCustomField;
    demographyId: number;
    classificationValueMappings: HrisClassificationValueMapping[];
  };

  export type SaveHrisDemographyCustomFieldMapping = {
    id: string | null;
    demographyCustomFieldId: string;
    demographyId: number;
    classificationValueMappings: HrisClassificationValueMapping[];
  };
}

export class CompanyApi {
  constructor(private client: AxiosInstance) {}

  create(client: CompanyPostEntry, logo?: File): Promise<CompanyDto> {
    const newClient = { ...client, logoFile: logo };
    return this.client
      .post<WithData<CompanyDto>>(`/api/v1/company`, jsonToFormData(newClient))
      .then(selectApiData);
  }

  getList(query: string): Promise<CompanyApi.GetListResponse> {
    return this.client
      .get(`/api/v1/company${query}`)
      .then((response) => selectResponseData(response, '_embedded.items', []));
  }

  getProjectList(query: string): Promise<ProjectPaginatedDto> {
    return this.client
      .get(`/api/v1/project${query}`)
      .then((response) => selectResponseData(response) || []);
  }

  updateEnabledLanguages(id: number, languages: string[]): Promise<CompanyApi.EnabledLocales> {
    return this.client.put(`/api/v1/company/${id}/enabled-locales`, {
      enabledLocales: languages,
    });
  }

  updateEnabledDashboardLanguages(
    id: number,
    languages: string[],
    isDashboardLanguagesEnabled: boolean,
    shouldTranslateComments: boolean,
  ): Promise<CompanyApi.EnabledLocales> {
    return this.client.put(`/api/v1/company/${id}/enabled-dashboard-locales`, {
      enabledDashboardLocales: languages,
      isDashboardLanguagesEnabled,
      shouldTranslateComments,
    });
  }

  getProfile(companyId: number): Promise<CompanyApi.CompanyProfile> {
    return this.client
      .get<WithData<CompanyApi.CompanyProfile>>(`/api/v1/company/${companyId}/profile`)
      .then(selectApiData);
  }

  updateProfile(companyId: number, profile: CompanyApi.CompanyProfile): Promise<void> {
    return this.client.put(`/api/v1/company/${companyId}/profile`, profile);
  }

  createMergeLinkToken(companyId: number): Promise<string> {
    return this.client
      .post<CompanyApi.MergeLinkToken>(`/api/v1/company/${companyId}/hris/link-token`)
      .then(selectResponseData)
      .then((result) => result.linkToken);
  }

  createMergeConnection(companyId: number, publicToken: string): Promise<void> {
    return this.client.post(`/api/v1/company/${companyId}/hris/connections`, {
      publicToken,
    });
  }

  getMergeConnections(companyId: number): Promise<CompanyApi.MergeConnection[]> {
    return this.client
      .get<CompanyApi.MergeConnection[]>(`/api/v1/company/${companyId}/hris/connections`)
      .then(selectResponseData);
  }

  getHrisDemographyCustomFields(
    connectionId: string,
  ): Promise<CompanyApi.HrisDemographyCustomField[]> {
    return this.client
      .get<
        CompanyApi.HrisDemographyCustomField[]
      >(`/api/v1/hris/connections/${connectionId}/demography-custom-fields`)
      .then(selectResponseData);
  }

  getHrisDemographyCustomFieldMappings(
    connectionId: string,
  ): Promise<CompanyApi.HrisDemographyCustomFieldMapping[]> {
    return this.client
      .get<
        CompanyApi.HrisDemographyCustomFieldMapping[]
      >(`/api/v1/hris/connections/${connectionId}/demography-custom-field-mappings`)
      .then(selectResponseData);
  }

  saveHrisDemographyCustomFieldMappings(
    connectionId: string,
    mappings: CompanyApi.SaveHrisDemographyCustomFieldMapping[],
  ): Promise<void> {
    return this.client.put(
      `/api/v1/hris/connections/${connectionId}/demography-custom-field-mappings`,
      { mappings },
    );
  }
}
